import mapboxgl from "mapbox-gl";

export const MAP_POI_ICON_IDS = {
  battery: "greensparks-poi-battery",
  pdu: "greensparks-poi-pdu",
  load: "greensparks-poi-load",
  generator: "greensparks-poi-generator",
  solar: "greensparks-poi-solar",
} as const;

// Category.name -> icon key, for classifying a power-source topology node's
// icon by what kind of device it actually is (see loadPowerSourceConnectors
// in mapboxHelpers.ts) instead of always rendering the battery icon.
export const MAP_POI_ICON_CATEGORY_KEY: Record<string, keyof typeof MAP_POI_ICON_IDS> = {
  Battery: "battery",
  Generator: "generator",
  "Power Distribution Unit": "pdu",
  "Solar Array": "solar",
};

export type MapPoiIconSizePreference = "none" | "small" | "large";

export const DEFAULT_MAP_POI_ICON_SIZE: MapPoiIconSizePreference = "small";

export const normalizeMapPoiIconSize = (
  value: unknown
): MapPoiIconSizePreference =>
  value === "none" || value === "large" || value === "small"
    ? value
    : DEFAULT_MAP_POI_ICON_SIZE;

const MAP_POI_ICON_COLOR = "#000000";
const MAP_POI_ICON_SIZE = 48;
const MAP_POI_ICON_PIXEL_RATIO = 2;

const MAP_POI_ICON_SVGS: Record<keyof typeof MAP_POI_ICON_IDS, string> = {
  battery: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
  <rect x="5" y="8" width="12" height="8" rx="2" fill="none" stroke="currentColor" stroke-width="2"/>
  <rect x="17.5" y="10.5" width="2" height="3" rx="0.5" fill="currentColor"/>
  <rect x="8" y="10.5" width="3" height="3" rx="0.5" fill="currentColor"/>
</svg>`,
  pdu: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="6" r="2" fill="currentColor"/>
  <circle cx="7" cy="18" r="2" fill="currentColor"/>
  <circle cx="12" cy="18" r="2" fill="currentColor"/>
  <circle cx="17" cy="18" r="2" fill="currentColor"/>
  <path d="M12 8 L12 11 M12 11 L7 16 M12 11 L12 16 M12 11 L17 16"
        fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  load: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
  <rect x="9" y="6" width="6" height="4" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
  <path d="M10.5 6 L10.5 4 M13.5 6 L13.5 4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  <path d="M12 10 L12 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  <circle cx="12" cy="16" r="2.5" fill="currentColor"/>
</svg>`,
  generator: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
  <rect x="4" y="9" width="16" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="2"/>
  <path d="M8 9 L8 6.5 M16 9 L16 6.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  <polygon points="13,10.5 10.3,14.5 11.8,14.5 10.7,17.5 14.2,13 12.4,13" fill="currentColor"/>
</svg>`,
  solar: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
  <rect x="4" y="7" width="16" height="11" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
  <path d="M4 12.5 L20 12.5 M12 7 L12 18" fill="none" stroke="currentColor" stroke-width="2"/>
</svg>`,
};

export type MapPoiIconKey = keyof typeof MAP_POI_ICON_IDS;

export const getMapPoiIconDataUrl = (iconKey: MapPoiIconKey, color = MAP_POI_ICON_COLOR) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(MAP_POI_ICON_SVGS[iconKey].replace(/currentColor/g, color))}`;

const svgToImageData = (svg: string): Promise<ImageData | null> =>
  new Promise((resolve) => {
    const image = new Image();
    const canvas = document.createElement("canvas");
    canvas.width = MAP_POI_ICON_SIZE;
    canvas.height = MAP_POI_ICON_SIZE;

    image.onload = () => {
      const context = canvas.getContext("2d");
      if (!context) {
        resolve(null);
        return;
      }

      context.clearRect(0, 0, MAP_POI_ICON_SIZE, MAP_POI_ICON_SIZE);
      context.drawImage(image, 0, 0, MAP_POI_ICON_SIZE, MAP_POI_ICON_SIZE);
      resolve(context.getImageData(0, 0, MAP_POI_ICON_SIZE, MAP_POI_ICON_SIZE));
    };
    image.onerror = () => resolve(null);

    const coloredSvg = svg.replace(/currentColor/g, MAP_POI_ICON_COLOR);
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(coloredSvg)}`;
  });

export const ensureMapPoiIcons = async (map: mapboxgl.Map) => {
  await Promise.all(
    (Object.keys(MAP_POI_ICON_IDS) as Array<keyof typeof MAP_POI_ICON_IDS>).map(async (iconKey) => {
      const imageId = MAP_POI_ICON_IDS[iconKey];
      if (map.hasImage(imageId)) {
        return;
      }

      const imageData = await svgToImageData(MAP_POI_ICON_SVGS[iconKey]);
      if (!imageData || map.hasImage(imageId)) {
        return;
      }

      map.addImage(imageId, imageData, { pixelRatio: MAP_POI_ICON_PIXEL_RATIO, sdf: true });
    })
  );
};
